import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  Grid,
  Paper,
  Divider,
  Button,
  Alert,
  Tooltip,
  IconButton,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import {
  HomeRounded,
  BusinessRounded,
  DescriptionRounded,
  CurrencyExchangeRounded,
} from "@mui/icons-material";
import { backend_api } from "../../../static/server";
import { useSelector, useDispatch } from "react-redux";
import {
  getAllBilles,
  updateBill,
} from "../../../redux/Apps/Lawyerapp/BillingSlice/BillingSlice";
import DrawerTemplate from "../Cases/DrawerTemplate";
import { useDialog } from "../../DialogContext";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import ReceiptRoundedIcon from "@mui/icons-material/ReceiptRounded";
import EditBillPreview from "./EditBillPreview";
import { DataGrid } from "@mui/x-data-grid";
import ServiceTimeForm from "../Activities/ActivitiesForms/ServiceTimeForm";
import ExpenseForm from "../Activities/ActivitiesForms/ExpenseForm";
import DriveFileRenameOutlineRoundedIcon from "@mui/icons-material/DriveFileRenameOutlineRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { formatteDateDay } from "../../../styles/styles";
import { toast } from "react-toastify";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import ContactApiField from "../Settings/FieldsUi/ContactApiField";
import MatterApiField from "../Settings/FieldsUi/MatterApiField";
import { ActivityApiField } from "../Settings/FieldsUi/ActivityApiField";
import { generateInvoiceNumber } from "../../../pages/LawyerPages/Billing/BillingPage";
import { getCurrencySymbol } from "../../../pages/LawyerPages/Settings/CurrencySetForm";

const EditBillForm = ({ editBillData }) => {
  // console.log("EditBillForm Data:", editBillData);
  const theme = useTheme();
  const {
    token,
    UserCurrencyList = [],
    defaultCurrency,
  } = useSelector((state) => state.user);
  const { showDialog, closeDialog } = useDialog();
  const [isUpdating, setIsUpdating] = useState(false);

  // Get available currencies from user's currency list
  const availableCurrencies = UserCurrencyList || [];

  // Determine the bill's currency - prioritize bill currency, then matter currency, then default
  const billCurrency =
    editBillData?.currency ||
    editBillData?.case?.currency ||
    defaultCurrency ||
    "USD";

  // console.log("Bill Currency:", billCurrency);
  // console.log("Available Currencies:", availableCurrencies);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firm_name:
        editBillData?.extra?.firm_name ||
        editBillData?.firm?.name ||
        "Your firm name",
      firm_address:
        editBillData?.extra?.firm_address ||
        editBillData?.firm?.address ||
        "firm address",
      firm_city:
        editBillData?.extra?.firm_city ||
        editBillData?.firm?.city ||
        "firm city",
      firm_state:
        editBillData?.extra?.firm_state ||
        editBillData?.firm?.state ||
        "firm state",
      firm_pincode:
        editBillData?.extra?.firm_pincode ||
        editBillData?.firm?.pin_code ||
        "firm pin code",
      firm_country:
        editBillData?.extra?.firm_country ||
        editBillData?.firm?.country ||
        "firm country",
      client: editBillData?.case?.case_related_person || "",
      case: editBillData?.case?.id || "",
      changeClientAddress: false,
      client_address:
        editBillData?.extra?.client_address ||
        editBillData?.case?.case_related_person?.address_1 ||
        "client address",
      client_city:
        editBillData?.extra?.client_city ||
        editBillData?.case?.case_related_person?.city ||
        "client city",
      client_state:
        editBillData?.extra?.client_state ||
        editBillData?.case?.case_related_person?.state ||
        "client state",
      client_pincode:
        editBillData?.extra?.client_pincode ||
        editBillData?.case?.case_related_person?.pincode ||
        "client pin code",
      client_country:
        editBillData?.extra?.client_country ||
        editBillData?.case?.case_related_person?.country ||
        "client country",
      issue_date: editBillData?.issue_date
        ? dayjs(editBillData.issue_date)
        : dayjs(),
      due_date: editBillData?.due_date
        ? dayjs(editBillData.due_date)
        : dayjs().add(30, "day"),
      invoice_num: editBillData?.id
        ? generateInvoiceNumber(editBillData.id)
        : "Auto-generated",
      status: editBillData?.status || "draft",
      // Add currency field
      currency: billCurrency,
    },
  });

  const watchedValues = watch();
  const client = watch("client");
  const changeClientAddress = watch("changeClientAddress");
  const changeFirmAddress = watch("changeFirmAddress");
  const selectedCurrency = watch("currency") || billCurrency;

  const dispatch = useDispatch();
  const [showPreview, setShowPreview] = useState(true);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const [editedExpenses, setEditedExpenses] = useState(
    editBillData?.expenses || [],
  );
  const existingActivitiesRef = useRef(editBillData?.expenses || []);
  const [selectedActivities, setSelectedActivities] = useState([]);

  // Get currency symbol for display
  const currencySymbol =
    getCurrencySymbol(selectedCurrency) || selectedCurrency;

  const getRowData = (expenses, dueDate, currency) =>
    (expenses || [])
      .filter((exp) => !exp.non_billable || exp.bill_entry_show)
      .map((exp, index) => ({
        id: exp.id || `temp_${index}`,
        type: exp.type || "--",
        due_date: formatteDateDay(dueDate),
        quantity: parseFloat(exp.quantity) || 0,
        rate: parseFloat(exp.hourly_rate || exp.rate) || 0,
        non_billable_amount: exp.non_billable
          ? parseFloat(exp.total_payment) || 0
          : 0,
        billable_amount: !exp.non_billable
          ? parseFloat(exp.total_payment) || 0
          : 0,
        currency: exp.currency || currency,
        currency_symbol:
          getCurrencySymbol(exp.currency || currency) ||
          exp.currency ||
          currency,
      }));

  const [tableData, setTableData] = useState(
    getRowData(editedExpenses, watchedValues?.due_date, selectedCurrency),
  );

  useEffect(() => {
    setTableData(
      getRowData(editedExpenses, watchedValues?.due_date, selectedCurrency),
    );
  }, [editedExpenses, watchedValues?.due_date, selectedCurrency]);

  const billableTotal = tableData
    .reduce((sum, e) => sum + (parseFloat(e.billable_amount) || 0), 0)
    .toFixed(2);
  const nonBillableTotal = tableData
    .reduce((sum, e) => sum + (parseFloat(e.non_billable_amount) || 0), 0)
    .toFixed(2);

  const handleEditRow = (rowId) => {
    const exp = editedExpenses.find(
      (ex, index) => (ex.id || index + 1) === rowId,
    );
    if (!exp) return;
    if (exp.type === "time") {
      showDialog(
        <ServiceTimeForm
          entryData={exp}
          editMode={true}
          defaultCaseId={editBillData?.case?.id}
          defaultCaseData={editBillData?.case}
          disableCase={true}
          onSuccess={handleUpdatedActivity}
        />,
        {
          dialogTitle: "Edit Time Entry",
        },
      );
    } else {
      showDialog(
        <ExpenseForm
          entryData={exp}
          editMode={true}
          defaultCaseId={editBillData?.case?.id}
          defaultCaseData={editBillData?.case}
          disableCase={true}
          defaultCategoryId={exp?.category?.id}
          disableCategory={true}
          onSuccess={handleUpdatedActivity}
        />,
        {
          dialogTitle: "Edit Expense Entry",
        },
      );
    }
  };

  const updateActivities = async (activities) => {
    try {
      await dispatch(
        updateBill({
          id: editBillData?.id,
          data: { expenses: activities.map((a) => a.id) },
          token,
        }),
      ).unwrap();
      existingActivitiesRef.current = activities;
      setEditedExpenses(activities);
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdatedActivity = async (activity) => {
    const updated = existingActivitiesRef.current.map((a) =>
      a.id === activity.id ? activity : a,
    );
    await updateActivities(updated);
    setActivityRefreshKey((k) => k + 1);
  };

  const handleDeleteActivity = async (rowId) => {
    const updated = existingActivitiesRef.current.filter((a) => a.id !== rowId);
    await updateActivities(updated);
  };

  const handleAddSelectedActivities = async () => {
    if (selectedActivities.length === 0) return;
    const updated = [...existingActivitiesRef.current, ...selectedActivities];
    await updateActivities(updated);
    setSelectedActivities([]);
  };

  const onSubmit = async (data) => {
    setIsUpdating(true);
    try {
      const extra = {};

      // Add firm details to `extra` if only temporary
      if (data.changeFirmAddress && !data.permanentlyUpdateFirm) {
        extra.firm_name = data.firm_name;
        extra.firm_address = data.firm_address;
        extra.firm_city = data.firm_city;
        extra.firm_state = data.firm_state;
        extra.firm_pincode = data.firm_pincode;
        extra.firm_country = data.firm_country;
      }

      // Add client details to `extra` if only temporary
      if (data.changeClientAddress && !data.permanentlyUpdateClient) {
        extra.client_address = data.client_address;
        extra.client_city = data.client_city;
        extra.client_state = data.client_state;
        extra.client_pincode = data.client_pincode;
        extra.client_country = data.client_country;
      }

      const formattedData = {
        ...data,
        client: data.client?.id || data.client,
        issue_date: data.issue_date
          ? dayjs(data.issue_date).format("YYYY-MM-DD")
          : null,
        due_date: data.due_date
          ? dayjs(data.due_date).format("YYYY-MM-DD")
          : null,
        expenses: editedExpenses.map((exp) => exp?.id).filter(Boolean),
        currency: data.currency || billCurrency, // Include currency in payload
        extra: extra,
      };

      delete formattedData.changeFirmAddress;
      delete formattedData.changeClientAddress;
      delete formattedData.permanentlyUpdateFirm;
      delete formattedData.permanentlyUpdateClient;

      await dispatch(
        updateBill({ id: editBillData?.id, data: formattedData, token }),
      ).unwrap();

      if (data.changeFirmAddress && data.permanentlyUpdateFirm) {
        if (editBillData?.firm?.id) {
          await axios.put(
            `${backend_api}firm/create-law-firm/${editBillData?.firm?.id}/`,
            {
              name: data.firm_name,
              address: data.firm_address,
              city: data.firm_city,
              state: data.firm_state,
              pin_code: data.firm_pincode,
              country: data.firm_country,
            },
            {
              headers: {
                Authorization: `Token ${token}`,
              },
            },
          );
          toast.success("Firm address permanently updated");
        } else {
          console.error("Firm ID not found for permanent update");
          toast.error("Could not update firm permanently - firm ID missing");
        }
      }

      if (data.changeClientAddress && data.permanentlyUpdateClient) {
        await axios.put(
          `${backend_api}contact/create/${data.client?.id || data.client}/`,
          {
            address_1: data.client_address,
            city: data.client_city,
            state: data.client_state,
            pincode: data.client_pincode,
            country: data.client_country,
          },
          {
            headers: {
              Authorization: `Token ${token}`,
            },
          },
        );
        toast.success("Client address permanently updated");
      }
    } catch (error) {
      console.error("Error updating bill:", error);

      if (error.response?.data?.message) {
        toast.error(`Error: ${error.response.data.message}`);
      } else if (error.message) {
        toast.error(`Error: ${error.message}`);
      } else {
        toast.error("An error occurred while updating the bill");
      }
    } finally {
      toast.success("Bill updated successfully!");
      setIsUpdating(false);
      closeDialog();
      reset();
      dispatch(
        getAllBilles({
          url: `${backend_api}firm-case/bill/?status=${editBillData?.status}`,
          token,
        }),
      );
    }
  };

  const tableColumns = [
    {
      field: "actions",
      headerName: "Action",
      width: 80,
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <Tooltip title="Edit Activity">
            <IconButton
              size="small"
              onClick={() => handleEditRow(params.row.id)}
            >
              <DriveFileRenameOutlineRoundedIcon
                sx={{ color: "primary.main", fontSize: "0.9rem" }}
              />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => handleDeleteActivity(params.row.id)}
          >
            <DeleteRoundedIcon
              sx={{ color: "text.secondary", fontSize: "0.9rem" }}
            />
          </IconButton>
        </>
      ),
    },
    { field: "type", headerName: "Type", width: 120 },
    { field: "due_date", headerName: "Due Date", width: 120 },
    { field: "quantity", headerName: "Qty", width: 100, type: "number" },
    {
      field: "rate",
      headerName: `Rate (${currencySymbol})`,
      width: 120,
      type: "number",
      valueFormatter: (params) => {
        const value = parseFloat(params.value) || 0;
        return value.toFixed(2);
      },
    },
    {
      field: "non_billable_amount",
      headerName: `Non-billable (${currencySymbol})`,
      width: 150,
      type: "number",
      valueFormatter: (params) => {
        const value = parseFloat(params.value) || 0;
        return value.toFixed(2);
      },
    },
    {
      field: "billable_amount",
      headerName: `Total Amount (${currencySymbol})`,
      width: 140,
      align: "right",
      type: "number",
      valueFormatter: (params) => {
        const value = parseFloat(params.value) || 0;
        return value.toFixed(2);
      },
    },
  ];

  return (
    <Box sx={{ p: "1rem", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Grid container spacing={3}>
        {/* Form Section */}
        <Grid
          item
          xs={12}
          lg={6}
          sx={{ display: { xs: showPreview ? "none" : "block", lg: "block" } }}
        >
          <Paper elevation={2} sx={{ p: 3, height: "100%" }}>
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              {/* Bill Currency Section */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: "primary.lighter",
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <CurrencyExchangeRounded
                    sx={{ color: "primary.main", mr: 1 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ fontSize: "1rem", fontWeight: 600 }}
                  >
                    Bill Currency
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="currency"
                      control={control}
                      rules={{ required: "Currency is required" }}
                      render={({ field }) => (
                        <FormControl fullWidth size="small">
                          <InputLabel>Currency *</InputLabel>
                          <Select
                            {...field}
                            label="Currency *"
                            error={!!errors.currency}
                          >
                            {availableCurrencies.length > 0 ? (
                              availableCurrencies.map((currency) => (
                                <MenuItem
                                  key={currency.shortForm || currency}
                                  value={currency.shortForm || currency}
                                >
                                  {getCurrencySymbol(
                                    currency.shortForm || currency,
                                  )}{" "}
                                  {currency.shortForm || currency} -{" "}
                                  {currency.name || currency}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem value={billCurrency}>
                                {getCurrencySymbol(billCurrency)} {billCurrency}
                              </MenuItem>
                            )}
                          </Select>
                          {errors.currency && (
                            <Typography color="error" variant="caption">
                              {errors.currency.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {selectedCurrency && (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          Bill currency:{" "}
                          <strong>
                            {getCurrencySymbol(selectedCurrency)}{" "}
                            {selectedCurrency}
                          </strong>
                          {editBillData?.case?.currency && (
                            <span>
                              {" "}
                              (Matter currency: {editBillData.case.currency})
                            </span>
                          )}
                        </Typography>
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Firm Details Section */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <BusinessRounded sx={{ color: "primary.main", mr: 1 }} />
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "1rem", fontWeight: 600 }}
                  >
                    Firm Details
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="firm_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Firm Name"
                          size="small"
                          fullWidth
                          disabled={true}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BusinessRounded sx={{ color: "#DEDEDE" }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="firm_address"
                      control={control}
                      rules={{ required: "Firm address is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Firm Address"
                          disabled={!changeFirmAddress}
                          error={!!errors.firm_address}
                          helperText={errors.firm_address?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <HomeRounded sx={{ color: "#DEDEDE" }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="firm_city"
                      control={control}
                      rules={{ required: "City is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="City"
                          size="small"
                          fullWidth
                          disabled={!changeFirmAddress}
                          error={!!errors.firm_city}
                          helperText={errors.firm_city?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="firm_state"
                      control={control}
                      rules={{ required: "State is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="State"
                          size="small"
                          fullWidth
                          disabled={!changeFirmAddress}
                          error={!!errors.firm_state}
                          helperText={errors.firm_state?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="firm_pincode"
                      control={control}
                      rules={{
                        required: "Pin code is required",
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Pin Code"
                          size="small"
                          fullWidth
                          disabled={!changeFirmAddress}
                          error={!!errors.firm_pincode}
                          helperText={errors.firm_pincode?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="firm_country"
                      control={control}
                      rules={{ required: "Country is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Country"
                          size="small"
                          fullWidth
                          disabled={!changeFirmAddress}
                          error={!!errors.firm_country}
                          helperText={errors.firm_country?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="changeFirmAddress"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch {...field} size="small" color="primary" />
                          }
                          label="Change firm address for this bill"
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.8rem",
                              fontWeight: 500,
                              color: "text.secondary",
                            },
                          }}
                        />
                      )}
                    />

                    {changeFirmAddress && (
                      <Box sx={{ mt: 2 }}>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          You are about to modify firm details. Choose whether
                          to update permanently or for this bill only.
                        </Alert>

                        <Controller
                          name="permanentlyUpdateFirm"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Switch
                                  {...field}
                                  size="small"
                                  color="warning"
                                />
                              }
                              label="Update firm details permanently in system"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  color: "text.secondary",
                                },
                              }}
                            />
                          )}
                        />

                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{ mt: 1, ml: 4 }}
                        >
                          {watchedValues.permanentlyUpdateFirm
                            ? "✓ Changes will be saved to your firm profile for future bills"
                            : "• Changes apply only to this bill"}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Bill Details Section */}
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <DescriptionRounded sx={{ color: "primary.main", mr: 1 }} />
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "1rem", fontWeight: 600 }}
                  >
                    Bill Details
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <ContactApiField
                      name="client"
                      label="Client"
                      control={control}
                      errors={errors}
                      token={token}
                      apiUrl={`${backend_api}contact/retrieve/`}
                      disabled
                      returnFullData
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Controller
                      name="case"
                      control={control}
                      rules={{ required: "Matter is required" }}
                      render={({ field }) => (
                        <MatterApiField
                          label="Matter"
                          value={field.value}
                          onChange={field.onChange}
                          errors={errors}
                          token={token}
                          apiUrl={`${backend_api}case/register/?case_related_person=${
                            typeof client === "object" ? client?.id : client
                          }`}
                          disabled
                          AddMatterFunc={({ onCreated }) =>
                            showDialog(
                              <DrawerTemplate onMatterCreated={onCreated} />,
                              {
                                dialogTitle: "Select Template",
                              },
                            )
                          }
                          editData={editBillData?.case || null}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Controller
                      name="issue_date"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(newValue) => {
                            const isValidDate =
                              newValue && dayjs(newValue).isValid();
                            field.onChange(
                              isValidDate ? dayjs(newValue) : null,
                            );
                          }}
                          slotProps={{
                            textField: {
                              label: "Issue Date",
                              size: "small",
                              variant: "outlined",
                              fullWidth: true,
                            },
                          }}
                          format="MM/DD/YYYY"
                          maxDate={dayjs()}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Controller
                      name="due_date"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(newValue) => {
                            const isValidDate =
                              newValue && dayjs(newValue).isValid();
                            field.onChange(
                              isValidDate ? dayjs(newValue) : null,
                            );
                          }}
                          slotProps={{
                            textField: {
                              label: "Due Date",
                              size: "small",
                              variant: "outlined",
                              fullWidth: true,
                            },
                          }}
                          format="MM/DD/YYYY"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="changeClientAddress"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch {...field} size="small" color="primary" />
                          }
                          label="Change client address for this bill"
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.8rem",
                              fontWeight: 500,
                              color: "text.secondary",
                            },
                          }}
                        />
                      )}
                    />

                    {changeClientAddress && (
                      <Box sx={{ mt: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          You are about to modify client address for "
                          {client && typeof client === "object"
                            ? client.first_name || client.last_name
                              ? `${client.first_name || ""} ${client.last_name || ""}`.trim()
                              : client.company_name ||
                                client.email ||
                                "selected client"
                            : client || "selected client"}
                          ". Choose whether to update their profile permanently
                          or for this bill only.
                        </Alert>

                        <Controller
                          name="permanentlyUpdateClient"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Switch {...field} size="small" color="info" />
                              }
                              label="Update client address permanently in system"
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  color: "text.secondary",
                                },
                              }}
                            />
                          )}
                        />

                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{ mt: 1, ml: 4 }}
                        >
                          {watchedValues.permanentlyUpdateClient
                            ? "✓ Changes will update this client's address in your system"
                            : "• Changes apply only to this bill"}
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  {changeClientAddress && (
                    <>
                      <Grid item xs={12}>
                        <Controller
                          name="client_address"
                          control={control}
                          rules={{ required: "Client address is required" }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              size="small"
                              label="Client Address"
                              error={!!errors.client_address}
                              helperText={errors.client_address?.message}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <HomeRounded sx={{ color: "#DEDEDE" }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <Controller
                          name="client_city"
                          control={control}
                          rules={{ required: "City is required" }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="City"
                              size="small"
                              fullWidth
                              error={!!errors.client_city}
                              helperText={errors.client_city?.message}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <Controller
                          name="client_state"
                          control={control}
                          rules={{ required: "State is required" }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="State"
                              size="small"
                              fullWidth
                              error={!!errors.client_state}
                              helperText={errors.client_state?.message}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <Controller
                          name="client_pincode"
                          control={control}
                          rules={{
                            required: "Pin code is required",
                            pattern: {
                              value: /^[0-9]{6}$/,
                              message: "Please enter a valid 6-digit pin code",
                            },
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Pin Code"
                              size="small"
                              fullWidth
                              error={!!errors.client_pincode}
                              helperText={errors.client_pincode?.message}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <Controller
                          name="client_country"
                          control={control}
                          rules={{ required: "Country is required" }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Country"
                              size="small"
                              fullWidth
                              error={!!errors.client_country}
                              helperText={errors.client_country?.message}
                            />
                          )}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
              <Divider sx={{ my: 3 }} />

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    gap: "1rem",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <ReceiptRoundedIcon sx={{ color: "primary.main", mr: 1 }} />
                    <Typography
                      variant="body1"
                      sx={{ fontSize: "1rem", fontWeight: 600 }}
                    >
                      Billable Activity (Currency: {selectedCurrency}{" "}
                      {currencySymbol})
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2, width: "100%", display: "flex", gap: 1 }}>
                    <ActivityApiField
                      name="activities"
                      token={token}
                      apiUrl={`${backend_api}firm-case/expense/?non_billable=false`}
                      placeholder="Search activities"
                      refreshKey={activityRefreshKey}
                      returnFullData
                      value={[
                        ...existingActivitiesRef.current,
                        ...selectedActivities,
                      ]}
                      onChange={async (val) => {
                        const value = val || [];
                        const valueIds = value.map((v) => v.id);
                        const currentIds = existingActivitiesRef.current.map(
                          (e) => e.id,
                        );
                        const removed = currentIds.filter(
                          (id) => !valueIds.includes(id),
                        );
                        if (removed.length > 0) {
                          await updateActivities(
                            existingActivitiesRef.current.filter((e) =>
                              valueIds.includes(e.id),
                            ),
                          );
                        }
                        const newActs = value.filter(
                          (v) => !currentIds.includes(v.id),
                        );
                        setSelectedActivities(newActs);
                      }}
                    />
                    {selectedActivities.length > 0 && (
                      <Box>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleAddSelectedActivities}
                        >
                          Add
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Alert severity="warning" size="small" sx={{ mb: 2 }}>
                  Editing billable activities here will update the original
                  activity records.
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "0.8rem", color: "text.secondary" }}
                  >
                    <li>
                      Removing an activity here will update the bill
                      immediately.
                    </li>
                  </Typography>
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box>
                      <DataGrid
                        rows={tableData}
                        columns={tableColumns}
                        autoHeight
                        hideFooter
                        disableSelectionOnClick
                        sx={{
                          "& .MuiDataGrid-cell:focus": {
                            outline: "none",
                          },
                          "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: theme.palette.grey[200],
                          },
                        }}
                      />
                      {tableData.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            p: 1,
                            backgroundColor: theme.palette.grey[100],
                            border: `1px solid ${theme.palette.divider}`,
                            borderTop: "none",
                          }}
                        >
                          <Box sx={{ fontWeight: 600, mr: 2 }}>Total:</Box>
                          <Box sx={{ fontWeight: 600, mr: 2 }}>
                            {currencySymbol} {nonBillableTotal}
                          </Box>
                          <Box sx={{ fontWeight: 600 }}>
                            {currencySymbol} {billableTotal}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mt: 4,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => reset()}
                  sx={{ minWidth: 120 }}
                >
                  Cancel
                </Button>
                <LoadingButton
                  loading={isUpdating}
                  variant="contained"
                  size="small"
                  type="submit"
                  sx={{ minWidth: 120 }}
                >
                  Save Bill
                </LoadingButton>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Preview Section */}
        <Grid
          item
          xs={12}
          lg={6}
          sx={{ display: { xs: showPreview ? "block" : "none", lg: "block" } }}
        >
          <Box sx={{ position: "sticky", top: 20, height: "100%" }}>
            <EditBillPreview
              editBillData={{ ...editBillData, expenses: editedExpenses }}
              values={watchedValues}
              changeClientAddress={changeClientAddress}
              currency={selectedCurrency}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EditBillForm;
